import * as React from "react";
import { Box, Typography } from "@mui/material";
import type { IEnvironment } from "../../../types/EnvironmentTypes";
import { EnvironmentBox } from "./EnvironmentBox";
import { EnvironmentLink } from "./EnvironmentLink";
import { useRef, useState, useEffect } from "react";

export interface EnvironmentPipelineProps {
  environments: IEnvironment[];
  onEnvironmentClick?: (environment: IEnvironment) => void;
  selectedItem?: IEnvironment;
  onLinkClick?: (srcEnvironment: IEnvironment, dstEnvironment: IEnvironment) => void;
}

interface BoxPositions {
  [environmentId: string]: { x: number; y: number };
}

const ENV_BOXES_POSITION_STORAGE_KEY = "environment-box-positions";

export const EnvironmentPipeline = ({
  environments,
  onEnvironmentClick,
  selectedItem,
  onLinkClick,
}: EnvironmentPipelineProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [initialBoxPositions, setInitialBoxPositions] = useState<BoxPositions>({});
  const [boxPositions, setBoxPositions] = useState<BoxPositions>({});
  const [boxDimensions, setBoxDimensions] = useState<Record<string, { width: number; height: number }>>({});
  const [highlightedLink, setHighlightedLink] = useState<string | undefined>(undefined);
  const [hoveredBox, setHoveredBox] = useState<string | undefined>(undefined);

  // Load saved positions from local storage when the component mounts
  useEffect(() => {
    try {
      const savedPositions = localStorage.getItem(ENV_BOXES_POSITION_STORAGE_KEY);
      if (savedPositions) {
        const parsedPositions = JSON.parse(savedPositions) as BoxPositions;
        setBoxPositions(parsedPositions);
      }
    } catch (error) {
      console.error("Failed to load saved positions from localStorage:", error);
    }
  }, []);

  // Group environments by stage
  const envByStage = React.useMemo(() => {
    const result: Record<number, IEnvironment[]> = {};

    // Only consider stages 3-9, non-terminated environments, and ignore the fakes ones like "DEF_DEFAULT"
    for (let stage = 3; stage <= 9; stage++) {
      const envsInStage = environments.filter(
        (env) => env.stage === stage && env.terminationDate === undefined && env.backendImageId !== undefined,
      );
      if (envsInStage.length > 0) {
        result[stage] = envsInStage;
      }
    }

    return result;
  }, [environments]);

  // Initialize positions for environments that don't have them yet
  React.useEffect(() => {
    const newInitialPositions: BoxPositions = {};
    let needsUpdate = false;

    let stageX = 0;
    const heightByStage: Record<string, number> = {};

    // Calculate the position of environments within each stage (in a 3-column grid for each stage)
    Object.entries(envByStage).forEach(([stage, stageEnvs]) => {
      const columnSize = stageEnvs.length < 9 ? 3 : Math.ceil(stageEnvs.length / 3);
      let stageWidth = 0;
      let stageHeight = 0;
      let columnX = stageX + 20;
      let columnWidth = 0;
      let rowY = 20;
      let previousRowHeight = 0;

      stageEnvs.forEach((env, index) => {
        const row = index % columnSize;
        if (row === 0) {
          columnX += columnWidth + 20;
          stageWidth += columnWidth + 20;
          columnWidth = 0;
          rowY = 20;
          previousRowHeight = 0;
        } else {
          rowY += previousRowHeight + 20;
        }

        const { width, height } = boxDimensions[env.name] || { width: 0, height: 0 };
        if (width !== 0 && height !== 0) {
          // Only use the calculated position if no saved position exists in local storage
          if (boxPositions[env.name]) {
            newInitialPositions[env.name] = boxPositions[env.name];
          } else {
            newInitialPositions[env.name] = {
              x: columnX,
              y: rowY,
            };
          }
          if (width > columnWidth) {
            columnWidth = width;
          }
          previousRowHeight = height;
        }

        if (rowY + previousRowHeight + 20 > stageHeight) {
          stageHeight = rowY + previousRowHeight + 20;
        }
      });
      stageWidth += columnWidth + 20;
      stageX += stageWidth + 50;
      heightByStage[stage] = stageHeight;
    });

    // Finally, center the vertical positions of environments within the pipeline
    let maxStageHeight = 0;
    Object.values(heightByStage).forEach((height) => {
      if (height > maxStageHeight) {
        maxStageHeight = height;
      }
    });
    Object.entries(envByStage).forEach(([stage, stageEnvs]) => {
      const stageHeight = heightByStage[stage];
      const offsetY = (maxStageHeight - stageHeight) / 2;

      stageEnvs.forEach((env) => {
        const { width, height } = boxDimensions[env.name] || { width: 0, height: 0 };
        if (width !== 0 && height !== 0) {
          // Similarly to the previous step, only adjust the Y position for items that don't have a saved position
          if (!boxPositions[env.name]) {
            newInitialPositions[env.name].y += offsetY;
          }
          if (
            newInitialPositions[env.name].x !== initialBoxPositions[env.name]?.x ||
            newInitialPositions[env.name].y !== initialBoxPositions[env.name]?.y
          ) {
            needsUpdate = true;
          }
        }
      });
    });

    if (needsUpdate) {
      setInitialBoxPositions(newInitialPositions);
      const updatePositions = (prev: BoxPositions): BoxPositions => {
        const merged = { ...newInitialPositions };
        // Preserve any positions that were explicitly dragged/saved
        Object.keys(prev).forEach((key) => {
          if (prev[key]) {
            merged[key] = prev[key];
          }
        });
        return merged;
      };
      setBoxPositions(updatePositions);
    }
  }, [envByStage, boxDimensions, initialBoxPositions, boxPositions]);

  const handleBoxDrag = (envName: string, x: number, y: number) => {
    setBoxPositions((prev) => {
      const newPositions = {
        ...prev,
        [envName]: { x, y },
      };

      try {
        localStorage.setItem(ENV_BOXES_POSITION_STORAGE_KEY, JSON.stringify(newPositions));
      } catch (error) {
        console.error("Failed to save positions to localStorage:", error);
      }

      return newPositions;
    });
  };

  const handleBoxSizeChange = (envName: string, width: number, height: number) => {
    setBoxDimensions((prev) => ({
      ...prev,
      [envName]: { width, height },
    }));
  };

  // Find related environments (same stage or 1 stage lower)
  const relatedEnvironments = React.useMemo(() => {
    if (!selectedItem) return [];

    const relatedEnvs: IEnvironment[] = [];
    const selectedStage = selectedItem.stage;

    envByStage[selectedStage]?.forEach((env) => {
      if (env.name !== selectedItem.name) {
        relatedEnvs.push(env);
      }
    });

    for (let i = selectedStage - 1; i >= 3; i--) {
      if (envByStage[i]) {
        relatedEnvs.push(...envByStage[i]);
        break;
      }
    }

    return relatedEnvs;
  }, [selectedItem, envByStage]);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          overflowX: "auto",
          overflowY: "auto",
          width: "100%",
          height: "100%",
          padding: 2,
          position: "relative",
        }}
      >
        {selectedItem &&
          relatedEnvironments.map((relatedEnv) => {
            // Only render links if we have positions for both boxes
            if (
              boxPositions[selectedItem.name] &&
              boxPositions[relatedEnv.name] &&
              boxDimensions[selectedItem.name] &&
              boxDimensions[relatedEnv.name]
            ) {
              // Determine color based on stage relationship
              const arrowColor = relatedEnv.stage === selectedItem.stage ? "#1976d2" : "#4caf50";

              return (
                <EnvironmentLink
                  key={`arrow-${selectedItem.name}-to-${relatedEnv.name}`}
                  start={{
                    ...boxPositions[selectedItem.name],
                    ...boxDimensions[selectedItem.name],
                  }}
                  end={{
                    ...boxPositions[relatedEnv.name],
                    ...boxDimensions[relatedEnv.name],
                  }}
                  highlighted={highlightedLink === `${selectedItem.name}-${relatedEnv.name}` && !hoveredBox}
                  color={arrowColor}
                  onMouseOver={() => {
                    setHighlightedLink(`${selectedItem.name}-${relatedEnv.name}`);
                  }}
                  onMouseOut={() => {
                    if (highlightedLink === `${selectedItem.name}-${relatedEnv.name}`) {
                      setHighlightedLink(undefined);
                    }
                  }}
                  onClick={() => onLinkClick?.(relatedEnv, selectedItem)}
                />
              );
            }
            return null;
          })}

        {environments
          .filter(
            (env) =>
              env.stage >= 3 && env.stage <= 9 && env.terminationDate === undefined && env.backendImageId !== undefined,
          )
          .map((env) => (
            <EnvironmentBox
              key={env.name}
              environment={env}
              initialPosition={{
                x: initialBoxPositions[env.name]?.x || 0,
                y: initialBoxPositions[env.name]?.y || 0,
              }}
              hovered={hoveredBox === env.name}
              onSizeChange={(width, height) => handleBoxSizeChange(env.name, width, height)}
              onMouseOver={() => {
                setHoveredBox(env.name);
              }}
              onMouseOut={() => {
                if (hoveredBox === env.name) {
                  setHoveredBox(undefined);
                }
              }}
              onDrag={(x, y) => handleBoxDrag(env.name, x, y)}
              onClick={onEnvironmentClick}
              stageRef={containerRef}
            />
          ))}

        {Object.keys(envByStage).length === 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
              color: "text.secondary",
              fontStyle: "italic",
            }}
          >
            <Typography variant="body1">No environments available in pipeline view (stages 3-9)</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
